/**
 * Back-office sign-in, shared by the review queue and the content library.
 *
 * Credentials go to sessionStorage, not localStorage: a shared ops machine
 * should not keep an admin key after the tab closes.
 */
import { useState } from "react";
import type { FormEvent } from "react";
import { Layout } from "./Layout";
import { writeReviewCredentials } from "../lib/session";
import type { ReviewCreds } from "../api/endpoints";

export function ReviewSignIn({ onSubmit }: { onSubmit: (creds: ReviewCreds) => void }) {
  const [mode, setMode] = useState<"token" | "adminKey">("adminKey");
  const [value, setValue] = useState("");

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!value.trim()) return;
    const creds: ReviewCreds = { mode, value: value.trim() };
    writeReviewCredentials(creds);
    onSubmit(creds);
  }

  return (
    <Layout showNav={false} showAccount={false} title="Review">
      <section className="section">
        <div className="wrap narrow">
          <div className="eyebrow">Back office</div>
          <h1 className="display" style={{ fontSize: "clamp(24px,5vw,36px)" }}>
            Reviewer sign-in
          </h1>
          <p className="lede">
            Reviewers sign in with their own platform account. The shared admin key also
            works, but its decisions are audited as <code>service:admin-key</code> rather
            than as a person.
          </p>

          <form className="panel" onSubmit={handleSubmit}>
            <div className="filter-row" style={{ marginBottom: 14 }}>
              <button
                type="button"
                className={`chip ${mode === "adminKey" ? "chip-active" : ""}`}
                onClick={() => setMode("adminKey")}
              >
                Admin key
              </button>
              <button
                type="button"
                className={`chip ${mode === "token" ? "chip-active" : ""}`}
                onClick={() => setMode("token")}
              >
                Platform token
              </button>
            </div>

            <div className="field">
              <label htmlFor="creds">
                {mode === "adminKey" ? "ADMIN_API_KEY" : "Platform access token"}
              </label>
              <input
                id="creds"
                type="password"
                value={value}
                autoComplete="off"
                placeholder={mode === "adminKey" ? "From the API's .env" : "eyJhbGciOi…"}
                onChange={(event) => setValue(event.target.value)}
              />
              <div className="hint">
                Kept for this browser tab only, never written to disk.
              </div>
            </div>

            <button type="submit" className="btn btn-primary" disabled={!value.trim()}>
              Open the queue
            </button>
          </form>
        </div>
      </section>
    </Layout>
  );
}
